import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Table, Card, Row, Col, Select, Progress, Tag } from "antd";
import { Api_Url } from "../../../config/config";
import axios from "axios";
import { ErrorToast } from "../../ToastComponent";

const { Option } = Select;

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];
const currentYear = new Date().getFullYear();
const YEARS = [currentYear - 1, currentYear, currentYear + 1];

const ROLE_LABELS = { 1: "Credit", 2: "Central Operations", 3: "Operations" };

const monthToQuarter = (month) => {
  // Financial year quarters: Q1=Apr-Jun, Q2=Jul-Sep, Q3=Oct-Dec, Q4=Jan-Mar
  if (month >= 4 && month <= 6) return 1;
  if (month >= 7 && month <= 9) return 2;
  if (month >= 10 && month <= 12) return 3;
  return 4; // Jan-Mar
};

const roleNameToId = (name) => {
  if (!name) return null;
  const n = String(name).toLowerCase().replace(/\s+/g, "");
  if (n === "credit") return 1;
  if (n === "centraloperation" || n === "centraloperations") return 2;
  if (n === "operation" || n === "operations") return 3;
  return null;
};

// Both target and achievement are stored in Cr. Format to 2 decimal places.
const fmtCr = (v) =>
  `₹ ${Number(v ?? 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} Cr.`;

const attainment = (t, a) => {
  const tv = Number(t ?? 0);
  const av = Number(a ?? 0);
  return tv > 0 ? Math.round((av / tv) * 100) : 0;
};

export const MyTargetPerformance = () => {
  const [myTargets, setMyTargets] = useState([]);
  const [branchTargets, setBranchTargets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [profileName, setProfileName] = useState("");
  const [filters, setFilters] = useState({
    month: new Date().getMonth() + 1,
    year: currentYear,
  });

  const user = useMemo(() => {
    try { return JSON.parse(sessionStorage.getItem("user")); } catch { return null; }
  }, []);
  const token = user?.accessToken;

  const roleName = (() => {
    try { return JSON.parse(sessionStorage.getItem("role")); } catch { return null; }
  })();
  const myRoleId = roleNameToId(roleName);

  const authHeader = useMemo(
    () => ({ headers: { Authorization: `Bearer ${token}` } }),
    [token]
  );

  const resolveProfileId = useCallback(async () => {
    const sessionProfile = (() => {
      try { return JSON.parse(sessionStorage.getItem("profile")); } catch { return null; }
    })();
    if (sessionProfile?.id) {
      setProfileName(`${sessionProfile.firstName || ""} ${sessionProfile.lastName || ""}`.trim());
      return Number(sessionProfile.id);
    }
    try {
      const payload = JSON.parse(atob(token.split(".")[1]));
      if (payload?.pid) return Number(payload.pid);
    } catch { /* ignore */ }
    if (user?.id) return Number(user.id);
    return null;
  }, [user, token]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.month) params.append("month", filters.month);
      if (filters.year)  params.append("year",  filters.year);

      const profileId = await resolveProfileId();

      // ── 1. Role-level target definitions ────────────────────────────────
      const roleParams = new URLSearchParams(params);
      roleParams.append("type", 2);
      if (myRoleId) roleParams.append("role", myRoleId);
      const defsRes = await axios.get(
        `${Api_Url}api/v1/Target?${roleParams.toString()}`, authHeader
      );
      const defs = defsRes?.data?.data || [];

      // ── 2. This employee's own achievements ──────────────────────────────
      let mine = [];
      if (profileId) {
        const mineParams = new URLSearchParams(params);
        mineParams.append("userProfileId", profileId);
        const mineRes = await axios.get(
          `${Api_Url}api/v1/EmployeeTarget?${mineParams.toString()}`, authHeader
        );
        mine = mineRes?.data?.data || [];
      }

      // Merge definitions with achievements (zero-fill if no record yet)
      const byTargetId = {};
      mine.forEach((m) => { byTargetId[m.targetId] = m; });
      setMyTargets(
        defs.map((d) => {
          const m = byTargetId[d.id];
          // Operations targets (role=3) are always 100 — show 100 even if not yet assigned
          const defaultTarget = d.role === 3 ? 100 : 0;
          return {
            key: `t-${d.id}`,
            targetId: d.id,
            targetName: d.name,
            role: d.role,
            targetValue: m ? m.targetValue : defaultTarget,
            achievementValue: m ? m.achievementValue : 0,
            openingValue: m ? m.openingValue : 0,
          };
        })
      );

      // ── 3. Branch targets (all) ──────────────────────────────────────────
      const branchRes = await axios.get(
        `${Api_Url}api/v1/BranchTarget?${params.toString()}`, authHeader
      );
      const allBranchTargets = branchRes?.data?.data || [];

      // ── 4. Employee's assigned branches via performance snapshot ─────────
      let assignedBranchIds = null;
      if (profileId) {
        try {
          const quarter = monthToQuarter(filters.month);
          const snapRes = await axios.get(
            `${Api_Url}api/v1/EmployeePerformanceSnapshot?quarter=${quarter}&year=${filters.year}`,
            authHeader
          );
          const mySnaps = (snapRes?.data?.data || []).filter(
            (s) => Number(s.userProfileId) === Number(profileId)
          );
          if (mySnaps.length > 0) {
            assignedBranchIds = new Set(mySnaps.map((s) => Number(s.branchId)));
          }
        } catch { /* fall through — show nothing if snapshot unavailable */ }
      }

      // If a snapshot exists, show only the employee's assigned branches.
      // If no snapshot is found for this quarter, show all branch targets
      // so data is never hidden just because the quarterly assignment is missing.
      setBranchTargets(
        assignedBranchIds
          ? allBranchTargets.filter((bt) => assignedBranchIds.has(Number(bt.branchId)))
          : allBranchTargets
      );
    } catch {
      ErrorToast("Failed to fetch your performance");
    } finally {
      setLoading(false);
    }
  }, [filters, myRoleId, authHeader, resolveProfileId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ── Column definitions ───────────────────────────────────────────────────

  const openingValueColumn = {
    title: "Opening Value (%)",
    dataIndex: "openingValue",
    render: (v, r) => {
      const name = (r.targetName || "").toLowerCase().replace(/\s+/g, "");
      const isBounceOrDelinq = name.includes("bouncing") || name.includes("delinq");
      return isBounceOrDelinq ? Number(v ?? 0).toLocaleString() + '%' : "—";
    },
  };

  const myColumns = [
    {
      title: "Target",
      dataIndex: "targetName",
      sorter: (a, b) => (a.targetName || "").localeCompare(b.targetName || ""),
    },
    ...(myRoleId === 1 ? [openingValueColumn] : []),
    {
      title: "Target Value (%)",
      dataIndex: "targetValue",
      render: (v) => Number(v ?? 0).toLocaleString() + '%',
    },
    {
      title: "Achieved",
      dataIndex: "achievementValue",
      render: (v) => Number(v ?? 0).toLocaleString(),
    },
    {
      title: "Attainment",
      key: "attainment",
      render: (_, r) => (
        <Progress
          percent={attainment(r.targetValue, r.achievementValue)}
          size="small"
          status={attainment(r.targetValue, r.achievementValue) >= 100 ? "success" : "active"}
          style={{ width: 160 }}
        />
      ),
    },
  ];

  const branchColumns = [
    {
      title: "Branch",
      dataIndex: "branchName",
      sorter: (a, b) => (a.branchName || "").localeCompare(b.branchName || ""),
    },
    {
      title: "Target",
      dataIndex: "targetName",
    },
    {
      title: "Target (Cr.)",
      dataIndex: "targetValue",
      align: "right",
      render: (v) => fmtCr(v),
    },
    {
      title: "Achieved (Cr.)",
      dataIndex: "achievementValue",
      align: "right",
      render: (v) => fmtCr(v),
    },
    {
      title: "Attainment",
      key: "attainment",
      render: (_, r) => {
        const pct = attainment(r.targetValue, r.achievementValue);
        return (
          <Progress
            percent={pct}
            size="small"
            status={pct >= 100 ? "success" : "active"}
            style={{ width: 160 }}
          />
        );
      },
    },
  ];

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <div className="page-wrapper">
      <div className="content container-fluid">

        {/* Header */}
        <div className="row mb-3">
          <div className="col-12 d-flex align-items-center justify-content-between flex-wrap">
            <div>
              <h3 className="mb-0">My Performance</h3>
              {profileName && <span className="text-muted">{profileName}</span>}
            </div>
            {myRoleId && <Tag color="blue">Role: {ROLE_LABELS[myRoleId]}</Tag>}
          </div>
        </div>

        {/* Month / Year filter */}
        <Card className="mb-4">
          <Row gutter={[16, 16]} align="middle">
            <Col xs={12} sm={8} md={5}>
              <Select
                value={filters.month}
                onChange={(v) => setFilters((p) => ({ ...p, month: v }))}
                style={{ width: "100%" }}
              >
                {MONTHS.map((m, i) => (
                  <Option key={i + 1} value={i + 1}>{m}</Option>
                ))}
              </Select>
            </Col>
            <Col xs={12} sm={8} md={5}>
              <Select
                value={filters.year}
                onChange={(v) => setFilters((p) => ({ ...p, year: v }))}
                style={{ width: "100%" }}
              >
                {YEARS.map((y) => (
                  <Option key={y} value={y}>{y}</Option>
                ))}
              </Select>
            </Col>
          </Row>
        </Card>

        {/* Role-level targets */}
        <Card
          className="mb-4"
          title={`My Targets${myRoleId ? ` — ${ROLE_LABELS[myRoleId]}` : ""}`}
          size="small"
        >
          <div className="table-responsive">
            <Table
              className="table-striped"
              columns={myColumns}
              dataSource={myTargets}
              rowKey="key"
              loading={loading}
              locale={{ emptyText: "No targets defined for your role this month" }}
              pagination={false}
              scroll={{ x: 700 }}
            />
          </div>
        </Card>

        {/* Branch targets — achievement already stored in DB as Cr. */}
        <Card title="Branch Targets (My Branches)" size="small">
          <div className="table-responsive">
            <Table
              className="table-striped"
              columns={branchColumns}
              dataSource={branchTargets}
              rowKey="id"
              loading={loading}
              locale={{ emptyText: "No branch targets for this month" }}
              pagination={false}
              scroll={{ x: 800 }}
              summary={(pageData) => {
                const totalTarget   = pageData.reduce((s, r) => s + Number(r.targetValue   ?? 0), 0);
                const totalAchieved = pageData.reduce((s, r) => s + Number(r.achievementValue ?? 0), 0);
                return (
                  <Table.Summary fixed>
                    <Table.Summary.Row style={{ fontWeight: "bold", background: "#fafafa" }}>
                      <Table.Summary.Cell index={0}>Total</Table.Summary.Cell>
                      <Table.Summary.Cell index={1} />
                      <Table.Summary.Cell index={2} align="right">{fmtCr(totalTarget)}</Table.Summary.Cell>
                      <Table.Summary.Cell index={3} align="right">{fmtCr(totalAchieved)}</Table.Summary.Cell>
                      <Table.Summary.Cell index={4}>
                        <Progress
                          percent={attainment(totalTarget, totalAchieved)}
                          size="small"
                          status={attainment(totalTarget, totalAchieved) >= 100 ? "success" : "active"}
                          style={{ width: 160 }}
                        />
                      </Table.Summary.Cell>
                    </Table.Summary.Row>
                  </Table.Summary>
                );
              }}
            />
          </div>
        </Card>

      </div>
    </div>
  );
};
